import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { TreeItemLayout } from '@fluentui/react-components/unstable'
import {
    Badge,
    Button,
    FlatTreeItem,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Spinner,
    tokens,
    Tooltip,
    useRestoreFocusTarget,
} from '@fluentui/react-components'
import {
    bundleIcon,
    EyeFilled,
    EyeRegular,
    FolderFilled,
    FolderOpenFilled,
    FolderRegular,
    MoreHorizontal20Filled,
    MoreHorizontal20Regular,
    OpenFilled,
    OpenRegular,
    RenameFilled,
    RenameRegular,
} from '@fluentui/react-icons'
import { getFileTypeIconAsUrl } from '@fluentui/react-file-type-icons'
import { useTranslation } from 'react-i18next'
import FormattedDateTime from '../Date/FormattedDateTime'
import { DocumentFlatTreeItemProps, FolderDriveItem } from './DocumentFlatTree'
import StealthLink, { stealthClick } from '../StealthLink'
import { Destination } from '../../context/AxiosContext'
import useAxios from '../../hooks/useAxios'
import { DocumentOpenType } from '../../context/GlobalContext'
import { getDocumentExtension } from '../../helpers/documentHelper'

const MoreIcon = bundleIcon(MoreHorizontal20Filled, MoreHorizontal20Regular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)
const PreviewIcon = bundleIcon(EyeFilled, EyeRegular)
const RenameIcon = bundleIcon(RenameFilled, RenameRegular)

export const useStyles = makeStyles({
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingVerticalS,
    },
    iconSizeSmall: {
        height: '16px',
        width: '16px',
        fontSize: '16px',
    },
    folderyellow: { color: tokens.colorPaletteYellowForeground1 },
    foldergrey: { color: tokens.colorPaletteAnchorBackground2 },
    folderdarkred: { color: tokens.colorPaletteRedForeground1 },
    folderlightred: { color: tokens.colorPaletteDarkOrangeBorder1 },
    folderdarkorange: { color: tokens.colorPalettePeachBorderActive },
    folderlightorange: { color: tokens.colorStatusWarningForegroundInverted },
    folderdarkgreen: { color: tokens.colorPaletteGreenForeground3 },
    folderlightgreen: { color: tokens.colorPaletteLightGreenForeground3 },
    folderdarkteal: { color: tokens.colorPaletteTealBorderActive },
    folderlightteal: { color: tokens.colorPaletteLightTealBackground2 },
    folderdarkblue: { color: tokens.colorPaletteBlueBorderActive },
    folderlightblue: { color: tokens.colorPaletteBlueBackground2 },
    folderdarkpurple: { color: tokens.colorPalettePurpleBorderActive },
    folderlightpurple: { color: tokens.colorPalettePurpleBackground2 },
    folderdarkpink: { color: tokens.colorPalettePinkBorderActive },
    folderlightpink: { color: tokens.colorPalettePinkBackground2 },
})

type SimpleDocumentFlatTreeItemProps = DocumentFlatTreeItemProps & {
    onPreviewClick?: (driveItem: FolderDriveItem) => void
    onRenameClick?: (driveItem: FolderDriveItem) => void
}

export default function SimpleDocumentFlatTreeItem(props: SimpleDocumentFlatTreeItemProps): ReactElement {
    const axios = useAxios()
    const focusTargetAttribute = useRestoreFocusTarget()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const itemExtension = useMemo(() => getDocumentExtension(props.item.name), [props.item])

    const isFolder = useMemo(() => props.itemType === 'branch', [props.item])
    const isFile = useMemo(() => props.itemType === 'leaf', [props.item])
    const isOpen = useMemo(() => {
        return [...props.flatTreeHook.getTreeProps().openItems].some(id => id === props.value)
    }, [props.item, props.flatTreeHook.getTreeProps().openItems])

    const folderColor = useMemo(() => {
        if (isFolder) {
            const color: string = props.item.folder?.['decorator']?.['iconColor']
            return styles[`folder${color?.toLowerCase()}`]
        }
        return undefined
    }, [])

    const itemIcon = useMemo(() => {
        if (isFolder) {
            if (isOpen) {
                return <FolderOpenFilled className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            } else if (folderColor) {
                return <FolderFilled className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            } else {
                return <FolderRegular className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            }
        } else {
            return (
                <img
                    alt={`${itemExtension}`}
                    className={styles.iconSizeSmall}
                    src={getFileTypeIconAsUrl({ extension: itemExtension, type: 2, imageFileType: 'svg' })}
                />
            )
        }
    }, [folderColor, isFolder, isOpen])

    const showFilePreview = isFile && props.onPreviewClick !== undefined
    const showFileMenu = isFile && props.onRenameClick !== undefined
    const showFolderRename = isFolder && props.onRenameClick !== undefined
    const showActions = showFilePreview || showFileMenu || showFolderRename

    return (
        <FlatTreeItem {...props} {...focusTargetAttribute}>
            <TreeItemLayout
                expandIcon={isFolder ? itemIcon : undefined}
                iconBefore={!isFolder && itemIcon}
                iconAfter={
                    isFolder &&
                    (props.isContentLoading ? (
                        <Spinner size={'extra-tiny'} />
                    ) : (
                        <Badge size={'small'} appearance={'tint'}>
                            {props.fileCount ?? '?'}
                        </Badge>
                    ))
                }
                aside={isFile && <FormattedDateTime date={props.item.lastModifiedDateTime} />}
                actions={
                    showActions ? (
                        <>
                            {isFile && (
                                <div className={styles.actions}>
                                    {showFilePreview && (
                                        <Tooltip relationship={'label'} withArrow content={translate('file.pivot.document.mutate.preview.button')}>
                                            <Button
                                                aria-label="Preview"
                                                appearance="subtle"
                                                icon={<PreviewIcon />}
                                                onClick={() => props.onPreviewClick?.(props.item)}
                                            />
                                        </Tooltip>
                                    )}
                                    {showFileMenu && (
                                        <Menu>
                                            <MenuTrigger disableButtonEnhancement>
                                                <Button aria-label="More options" appearance="subtle" icon={<MoreIcon />} />
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem
                                                        icon={<OpenIcon />}
                                                        onClick={() => {
                                                            stealthClick(
                                                                {
                                                                    id: props.item.id,
                                                                    destination: Destination.Documents,
                                                                    documentsOpen: DocumentOpenType.Open,
                                                                },
                                                                axios,
                                                            )
                                                        }}
                                                    >
                                                        {translate('action.open')}
                                                    </MenuItem>
                                                    <MenuItem icon={<RenameIcon />} onClick={() => props.onRenameClick?.(props.item)}>
                                                        {translate('file.pivot.document.mutate.rename.button')}
                                                    </MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    )}
                                </div>
                            )}
                            {showFolderRename && (
                                <div className={styles.actions}>
                                    <Tooltip relationship={'label'} withArrow content={translate('file.pivot.document.mutate.rename.button')}>
                                        <Button
                                            aria-label="Rename"
                                            appearance="subtle"
                                            icon={<RenameIcon />}
                                            onClick={() => props.onRenameClick?.(props.item)}
                                        />
                                    </Tooltip>
                                </div>
                            )}
                        </>
                    ) : undefined
                }
            >
                {isFolder && props.item.displayName}
                {isFile && (
                    <StealthLink id={props.item.id} destination={Destination.Documents}>
                        {props.item.displayName}
                    </StealthLink>
                )}
            </TreeItemLayout>
        </FlatTreeItem>
    )
}
